import React from 'react';
import { Form } from 'react-final-form';
import { Box } from '@mui/material';

import { TextField, snackbar } from '@occc-app/design-system';
import { ConfirmDialog, LinearProgress } from '@occc/ui-design-system';
import { LocationGroupFragment } from '@occc/graphql';
import { useLocationGroupUpdateMutation } from '@occc-app/dataAccessLayer/locationGroup/mutations';

import { validateLocationGroup } from './validation';
import { LocationGroupFormValues } from './types';

interface LocationGroupUpdateDialogProps {
  locationGroup: LocationGroupFragment;
  open: boolean;
  onClose: () => void;
}

export const LocationGroupUpdateDialog: React.FC<LocationGroupUpdateDialogProps> = ({
  locationGroup,
  open,
  onClose,
}) => {
  const onUpdateCompleted = React.useCallback(() => {
    snackbar.success('Location Group was updated');
    onClose();
  }, [onClose]);

  const onUpdateError = React.useCallback(() => {
    snackbar.error('Unable to modify Location Group name. Please try a different name.');
  }, []);

  const [updateLocationGroup, { loading: updateLoading }] = useLocationGroupUpdateMutation({
    onCompleted: onUpdateCompleted,
    onError: onUpdateError,
  });

  const onSubmit = async (values: LocationGroupFormValues) => {
    updateLocationGroup({
      variables: {
        input: {
          uuid: locationGroup.uuid,
          name: values.name,
        },
      },
    });
  };

  const initialValues = React.useMemo(
    () => ({
      name: locationGroup.name,
    }),
    [locationGroup],
  );

  const renderForm = React.useCallback(() => {
    return <TextField name='name' label='Location Group Name' />;
  }, []);

  return (
    <Form<LocationGroupFormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={validateLocationGroup}
      render={({ handleSubmit, valid, dirty }) => {
        return (
          <ConfirmDialog
            dialogTitle='Rename Location Group'
            onClose={onClose}
            open={open}
            maxWidth='sm'
            fullWidth
            confirmButtonText='Rename group'
            confirmButtonProps={{
              onClick: handleSubmit,
              disabled: !valid || !dirty || updateLoading,
            }}
          >
            {updateLoading && <LinearProgress />}
            <Box mt={1}>{renderForm()}</Box>
          </ConfirmDialog>
        );
      }}
    />
  );
};
